import * as React from "react"
import { chipVariants } from "@heroui/styles"

import { cn } from "../lib/utils"

/**
 * Status labels on HeroUI's Chip (its pill shape and size), kept to the
 * site's rule: the label stays neutral and the tone sits in a dot, so a list
 * of statuses stays calm. `default` is the quietest; `outline` is for closed,
 * archived and anything turned off on purpose.
 */
type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "success"
  | "warning"
  | "info"
  | "outline"
  | "ghost"
  | "link"

const dotClass: Partial<Record<BadgeVariant, string>> = {
  destructive: "bg-danger",
  success: "bg-success",
  warning: "bg-warning",
  info: "bg-info",
}

function badgeVariants({
  variant = "default",
  className,
}: { variant?: BadgeVariant; className?: string } = {}) {
  const tone =
    variant === "outline" || variant === "ghost" || variant === "link"
      ? { variant: "tertiary" as const }
      : variant === "default"
        ? { variant: "secondary" as const }
        : { variant: "soft" as const }
  return cn(
    chipVariants({ color: "default", size: "md", ...tone }).base(),
    "items-center gap-1.5 whitespace-nowrap [&>svg]:size-3",
    variant === "default" ? "text-foreground" : "text-muted-foreground",
    variant === "outline" && "border border-border",
    className
  )
}

function Badge({
  className,
  variant = "default",
  children,
  ...props
}: React.ComponentProps<"span"> & { variant?: BadgeVariant }) {
  const dot = dotClass[variant]
  return (
    <span
      data-slot="badge"
      data-variant={variant}
      className={badgeVariants({ variant, className })}
      {...props}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", dot)}
        />
      )}
      {children}
    </span>
  )
}

export { Badge, badgeVariants }
export type { BadgeVariant }
